/**
 * About page media slots. Drop files under `apps/web/public/about-media/` and set
 * `imageSrc` / `videoSrc` here. Optional `videoSrc` auto-plays in the lightbox.
 */
export type AboutMediaItem = {
  id: string;
  /** Shown under the image or video in the lightbox. */
  caption: string;
  /** Placeholder label while `imageSrc` is unset. */
  placeholderLabel: string;
  /** e.g. "/about-media/ai-lessons.png" */
  imageSrc?: string;
  /** e.g. "/about-media/ai-lessons.mp4" — preferred in the lightbox when set. */
  videoSrc?: string;
};

export const ABOUT_STORY_MEDIA: AboutMediaItem = {
  id: "story-ms-leonard-frog",
  caption: "Ms. Leonard waiting for a ferry next to a frog who's ready to learn Python",
  placeholderLabel: "Photo: Ms. Leonard and the frog with backpack statue",
  imageSrc: "/about-media/story-ms-leonard-frog.jpg",
};

export const ABOUT_SPOTLIGHT_MEDIA: readonly AboutMediaItem[] = [
  {
    id: "ai-lessons",
    caption: "AI lesson studio: describe a lesson in plain English and get starter code, a guided tour, and checks.",
    placeholderLabel: "Screenshot: AI lesson studio",
    imageSrc: "/about-media/ai-lessons.png",
    videoSrc: "/about-media/ai-lessons.mp4",
  },
  {
    id: "classroom-ide",
    caption: "Student IDE with a step-by-step guided tour running in the browser.",
    placeholderLabel: "Screenshot: student IDE with guided tour",
    imageSrc: "/about-media/classroom-ide.png",
    videoSrc: "/about-media/classroom-ide.mp4",
  },
  {
    id: "progress",
    caption: "Cohort progress dashboard showing completion, in-progress work, and where students get stuck.",
    placeholderLabel: "Screenshot: cohort progress dashboard",
    imageSrc: "/about-media/progress.png",
    videoSrc: "/about-media/progress.mp4",
  },
];

export const ABOUT_CAROUSEL_MEDIA: readonly AboutMediaItem[] = [
  {
    id: "library",
    caption: "Searchable lesson library with reusable lessons across terms.",
    placeholderLabel: "Screenshot: lesson library",
    imageSrc: "/about-media/library.png",
    videoSrc: "/about-media/library.mp4",
  },
  {
    id: "cohorts",
    caption: "Cohort roster, join code, and release schedule for a class.",
    placeholderLabel: "Screenshot: cohort roster and join code",
    imageSrc: "/about-media/cohorts.png",
    videoSrc: "/about-media/cohorts.mp4",
  },
  {
    id: "courses",
    caption: "Course builder: ordered lesson blueprints from your library.",
    placeholderLabel: "Screenshot: course builder",
    imageSrc: "/about-media/courses.png",
    videoSrc: "/about-media/courses.mp4",
  },
  {
    id: "playgrounds",
    caption: "Playground IDE for free-form coding projects beyond assignments.",
    placeholderLabel: "Screenshot: playground IDE",
    imageSrc: "/about-media/playgrounds.png",
    videoSrc: "/about-media/playgrounds.mp4",
  },
  {
    id: "students-free",
    caption: "Student lessons home: always free for kids. Teacher subscriptions are billed securely through Stripe; we never see your payment info, and you can cancel anytime.",
    placeholderLabel: "Screenshot: student lessons home",
    imageSrc: "/about-media/students-free.png",
    videoSrc: "/about-media/students-free.mp4",
  },
];

/** Full product demo rendered by `scripts/build_product_demo.py` → `public/about-media/product-demo.mp4`. */
export const ABOUT_PRODUCT_DEMO_VIDEO = "/about-media/product-demo.mp4";

/** Lookup spotlight media by spotlight id. */
export function spotlightMedia(id: string): AboutMediaItem {
  return ABOUT_SPOTLIGHT_MEDIA.find((item) => item.id === id) ?? {
    id,
    caption: id,
    placeholderLabel: `Screenshot: ${id}`,
  };
}

/** Lookup carousel media by slide id. */
export function carouselMedia(id: string): AboutMediaItem {
  return ABOUT_CAROUSEL_MEDIA.find((item) => item.id === id) ?? {
    id,
    caption: id,
    placeholderLabel: `Screenshot: ${id}`,
  };
}
